import React from 'react';
import { Trophy, Star, Heart, Sparkles, Crown } from 'lucide-react';
import { Reveal } from '../../hooks/useReveal';

/**
 * Awards & Recognition — animated medal-style badges at the bottom of the
 * home page. Each badge:
 *   • Gold/silver/rose gradient medal disc with a rotating sheen ring
 *   • Floating sparkles around the icon
 *   • Hover tilt + glow
 *   • A small "ribbon" tag beneath the medal with the award name
 */
const AWARDS = [
  {
    key: 'best-pest-control',
    icon: Trophy,
    title: 'Best Pest Control Company',
    medalFrom: '#FFE082',
    medalVia: '#F59E0B',
    medalTo: '#B45309',
    ringFrom: 'rgba(251, 191, 36, 0.7)',
    ringTo: 'rgba(180, 83, 9, 0.3)',
    ribbon: 'from-amber-500 via-amber-400 to-amber-600',
  },
  {
    key: 'top-rated',
    icon: Star,
    title: 'Top Rated Service Provider',
    medalFrom: '#E5E7EB',
    medalVia: '#9CA3AF',
    medalTo: '#4B5563',
    ringFrom: 'rgba(148, 163, 184, 0.7)',
    ringTo: 'rgba(71, 85, 105, 0.3)',
    ribbon: 'from-slate-400 via-slate-300 to-slate-500',
  },
  {
    key: 'customer-choice',
    icon: Heart,
    title: 'Customer Choice Award',
    medalFrom: '#FBCFE8',
    medalVia: '#EC4899',
    medalTo: '#9D174D',
    ringFrom: 'rgba(236, 72, 153, 0.7)',
    ringTo: 'rgba(157, 23, 77, 0.3)',
    ribbon: 'from-rose-500 via-pink-400 to-rose-600',
  },
];

export default function AwardsRecognition() {
  return (
    <section
      className="relative py-20 sm:py-24 overflow-hidden"
      data-testid="awards-recognition-section"
      style={{
        backgroundImage:
          'radial-gradient(ellipse at top, rgba(251,191,36,0.18) 0%, transparent 55%), ' +
          'linear-gradient(180deg, #0A1A2E 0%, #07101E 60%, #050B17 100%)',
      }}
    >
      {/* Floating confetti specks */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <span
            key={i}
            className="absolute block rounded-full"
            style={{
              width: `${3 + (i % 4)}px`,
              height: `${3 + (i % 4)}px`,
              left: `${(i * 7.3) % 100}%`,
              top: `${(i * 11.7) % 100}%`,
              background:
                i % 3 === 0
                  ? 'rgba(251,191,36,0.7)'
                  : i % 3 === 1
                  ? 'rgba(244,114,182,0.55)'
                  : 'rgba(148,163,184,0.55)',
              filter: 'blur(0.5px)',
              opacity: 0.7,
              animation: `awardFloat ${5 + (i % 5)}s ease-in-out ${i * 0.2}s infinite alternate`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal>
          <div className="text-center mb-14 sm:mb-20">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 backdrop-blur-md border border-amber-300/30 text-[11px] uppercase tracking-[0.25em] font-bold text-amber-300 mb-4"
              data-testid="awards-eyebrow"
            >
              <Crown size={13} /> Awards & Recognition
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3"
              style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
            >
              Honoured by{' '}
              <span className="italic bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
                Customers & Industry
              </span>
            </h2>
            <p className="text-sm sm:text-base text-slate-300/85 max-w-2xl mx-auto leading-relaxed">
              Years of consistent, safe and effective service have earned us multiple recognitions —
              but the real award is the trust of every family and business we&apos;ve protected.
            </p>
          </div>
        </Reveal>

        {/* Medals — 3 awards (titles only, no year/subtitle) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 sm:gap-14 lg:gap-16 justify-items-center">
          {AWARDS.map((a, i) => {
            const Icon = a.icon;
            return (
              <Reveal key={a.key} delay={i * 0.15}>
                <div
                  data-testid={`award-badge-${a.key}`}
                  className="group relative flex flex-col items-center text-center"
                >
                  {/* Medal disc wrapper */}
                  <div className="relative w-52 h-52 sm:w-60 sm:h-60 mb-6 transition-transform duration-500 group-hover:-translate-y-2">
                    {/* Spinning sheen ring */}
                    <div
                      className="absolute inset-[-6px] rounded-full opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: `conic-gradient(from 0deg, ${a.ringFrom}, transparent 30%, ${a.ringTo} 60%, transparent 80%, ${a.ringFrom})`,
                        animation: 'awardSheen 6s linear infinite',
                        filter: 'blur(2px)',
                      }}
                      aria-hidden
                    />

                    {/* Outer halo */}
                    <div
                      className="absolute inset-[-22px] rounded-full opacity-50 group-hover:opacity-90 transition-opacity duration-500"
                      style={{
                        background: `radial-gradient(circle, ${a.ringFrom} 0%, transparent 65%)`,
                        filter: 'blur(16px)',
                      }}
                      aria-hidden
                    />

                    {/* Medal face */}
                    <div
                      className="relative w-full h-full rounded-full flex items-center justify-center shadow-2xl"
                      style={{
                        background: `radial-gradient(circle at 30% 25%, ${a.medalFrom} 0%, ${a.medalVia} 45%, ${a.medalTo} 100%)`,
                        boxShadow:
                          'inset 0 4px 14px rgba(255,255,255,0.45), inset 0 -8px 18px rgba(0,0,0,0.35), 0 18px 40px -10px rgba(0,0,0,0.55)',
                      }}
                    >
                      {/* Engraved inner circle */}
                      <div
                        className="absolute inset-3 rounded-full border-[3px] border-white/30"
                        style={{ boxShadow: 'inset 0 0 12px rgba(0,0,0,0.3)' }}
                        aria-hidden
                      />
                      {/* Notches around medal */}
                      {Array.from({ length: 12 }).map((_, idx) => (
                        <span
                          key={idx}
                          className="absolute top-1/2 left-1/2 w-1 h-3 bg-white/35 rounded-full"
                          style={{
                            transform: `translate(-50%, -50%) rotate(${idx * 30}deg) translateY(-95px)`,
                          }}
                          aria-hidden
                        />
                      ))}

                      {/* Icon */}
                      <Icon
                        size={68}
                        strokeWidth={1.6}
                        className="relative text-white drop-shadow-[0_3px_6px_rgba(0,0,0,0.4)]"
                      />

                      {/* Sparkles */}
                      <Sparkles
                        size={18}
                        className="absolute top-4 right-6 text-white/90 animate-pulse"
                        aria-hidden
                      />
                      <Sparkles
                        size={14}
                        className="absolute bottom-8 left-6 text-white/80 animate-pulse"
                        style={{ animationDelay: '0.6s' }}
                        aria-hidden
                      />
                    </div>

                    {/* Hanging ribbon (V-shape) */}
                    <div
                      className={`absolute -bottom-7 left-1/2 -translate-x-1/2 w-24 h-12 bg-gradient-to-b ${a.ribbon} shadow-lg`}
                      style={{
                        clipPath:
                          'polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%)',
                      }}
                      aria-hidden
                    />
                  </div>

                  {/* Title */}
                  <h3
                    className="text-xl sm:text-2xl font-bold text-white mt-4 leading-tight"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    {a.title}
                  </h3>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes awardSheen {
          to { transform: rotate(360deg); }
        }
        @keyframes awardFloat {
          0%   { transform: translateY(0)   scale(1);   opacity: 0.4; }
          100% { transform: translateY(-18px) scale(1.15); opacity: 0.9; }
        }
      `}</style>
    </section>
  );
}
